import React, {useEffect, useRef, useState} from 'react';
import {FiTrash2, FiX} from 'react-icons/fi';
import './GalleryModal.css';
import AttachmentService from '../../services/AttachmentService';
import AuthImage from '../authImage/AuthImage';
import ImageViewer from '../imageViewer/ImageViewer'; // Import the new ImageViewer

function GalleryModal({isOpen, onClose, initialImages = [], onSave, customerId}) {
    const [activeTab, setActiveTab] = useState('imagini');
    const [newImages, setNewImages] = useState([]);
    const [photoAttachments, setPhotoAttachments] = useState([]);
    const [projectAttachments, setProjectAttachments] = useState([]);
    const [deletedImageIds, setDeletedImageIds] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [imageViewerOpen, setImageViewerOpen] = useState(false);
    const [viewerImages, setViewerImages] = useState([]);
    const [viewerInitialIndex, setViewerInitialIndex] = useState(0);
    const fileInputRef = useRef(null);

    // Initialize modal state when opened
    useEffect(() => {
        if (isOpen) {
            // Reset state
            setNewImages([]);
            setDeletedImageIds([]);

            // If we have a customer ID, fetch attachments
            if (customerId) {
                fetchAttachments();
            }
        }
    }, [isOpen, customerId, initialImages]);

    // Fetch attachments from server
    const fetchAttachments = async () => {
        if (!customerId) return;

        try {
            setIsLoading(true);

            // Fetch photo attachments
            const photos = await AttachmentService.getAttachments(customerId, 'PHOTO');
            setPhotoAttachments(photos);

            // Fetch project (drawing) attachments
            const projects = await AttachmentService.getAttachments(customerId, 'PROJECT');
            setProjectAttachments(projects);

            console.log("Fetched attachments:", {photos, projects});
        } catch (error) {
            console.error('Error fetching attachments:', error);
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    const handleImageClick = (image, index) => {
        // Get the current tab's images
        const currentImages = activeTab === 'proiecte' ? projectAttachments : photoAttachments;

        // Open the image viewer with all images from current tab
        setViewerImages(currentImages);
        setViewerInitialIndex(index);
        setImageViewerOpen(true);
    };

    const handleDeleteImage = (e, image) => {
        e.stopPropagation(); // Prevent triggering onClick of parent container

        // Add to deleted images list if it has an attachId (meaning it exists on the server)
        if (image.attachId) {
            setDeletedImageIds([...deletedImageIds, image.attachId]);
        }

        // If it's a new image (not yet saved to server), remove it from new images
        if (newImages.some(img => img.id === image.id)) {
            setNewImages(newImages.filter(img => img.id !== image.id));
        }

        // Update the appropriate attachments list
        if (activeTab === 'imagini') {
            setPhotoAttachments(photoAttachments.filter(img => img.id !== image.id));
        } else {
            setProjectAttachments(projectAttachments.filter(img => img.id !== image.id));
        }
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        // Create URL objects for each file for preview
        const newImagePreviews = files.map(file => ({
            id: Date.now() + Math.random(), // Create a unique ID
            url: URL.createObjectURL(file),
            file: file, // Store the file object for later upload
            fileName: file.name,
            attachType: activeTab === 'imagini' ? 'PHOTO' : 'PROJECT'
        }));

        // Add to new images
        setNewImages([...newImages, ...newImagePreviews]);

        // Add to the appropriate attachments list for display
        if (activeTab === 'imagini') {
            setPhotoAttachments([...photoAttachments, ...newImagePreviews]);
        } else {
            setProjectAttachments([...projectAttachments, ...newImagePreviews]);
        }

        // Reset the file input
        e.target.value = '';
    };

    const handleUploadClick = () => {
        fileInputRef.current.click();
    };

    const handleSave = () => {
        // We only report the changes back to the parent component
        // without performing any API calls - the parent will do that on final save

        // Return the new images, and deleted image IDs
        if (onSave) {
            onSave({
                newImages,
                deletedImageIds
            });
        }

        onClose();
    };

    // Combine images according to active tab
    const currentAttachments = activeTab === 'proiecte' ? projectAttachments : photoAttachments;

    return (
        <div className="gallery-modal-overlay">
            <div className="gallery-modal-content">
                <button className="gallery-modal-close" onClick={onClose}>
                    <FiX/>
                </button>

                <div className="gallery-modal-tabs">
                    <button
                        className={`gallery-tab ${activeTab === 'proiecte' ? 'active' : ''}`}
                        onClick={() => handleTabChange('proiecte')}
                    >
                        Proiecte
                    </button>
                    <button
                        className={`gallery-tab ${activeTab === 'imagini' ? 'active' : ''}`}
                        onClick={() => handleTabChange('imagini')}
                    >
                        Imagini mobilă
                    </button>
                </div>

                <div className="gallery-modal-body">
                    {isLoading ? (
                        <div className="gallery-loading">Se încarcă imaginile...</div>
                    ) : (
                        <div className="gallery-grid">
                            {currentAttachments.length === 0 ? (
                                <div className="no-attachments">Nu există atașamente.</div>
                            ) : (
                                currentAttachments.map((image,index) => (
                                    <div
                                        key={image.id}
                                        className={`gallery-item`}
                                        onClick={() => handleImageClick(image, index)}
                                    >
                                        {/* Use the AuthImage component instead of the standard img tag */}
                                        <AuthImage
                                            src={image.url}
                                            alt={image.fileName || "Gallery item"}
                                        />
                                        <button
                                            className="delete-image-btn"
                                            onClick={(e) => handleDeleteImage(e, image)}
                                            aria-label="Delete image"
                                        >
                                            <FiTrash2/>
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>

                <div className="gallery-modal-footer">
                    <button
                        className="gallery-save-button"
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        {isSaving ? 'Se salvează...' : 'Salvează'}
                    </button>
                    <button className="gallery-upload-button" onClick={handleUploadClick}>
                        Încarcă
                    </button>
                    {/* Hidden file input */}
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{display: 'none'}}
                        multiple
                        accept="image/*"
                        onChange={handleFileChange}
                    />
                </div>
            </div>
            <ImageViewer
                isOpen={imageViewerOpen}
                onClose={() => setImageViewerOpen(false)}
                images={viewerImages}
                initialIndex={viewerInitialIndex}
            />
        </div>
    );
}

export default GalleryModal;